import type { KpoaIncidentRow } from '../services/kpoaIncidentData';
import { formatMedErrorEventDate, toDateFromFirestore } from './medErrorDateThai';

const KPOA_EXPORT_HEADERS = [
  'วันที่เกิดเหตุการณ์',
  'หมวดเหตุการณ์',
  'เดือนเหตุการณ์',
  'ไตรมาส',
  'ปีงบประมาณ',
  'หน่วยงาน',
  'Ward',
  'IPD/OPD',
  'บทบาท',
  'ประเภทการบาดเจ็บ',
  'ระดับความรุนแรง',
  'สาเหตุหลัก',
  'อายุงาน (ปี)',
  'อายุ (ปี)',
  'หมายเหตุ',
];

const formatOptionalNumber = (value: number | undefined): string =>
  typeof value === 'number' && Number.isFinite(value) ? String(value) : '—';

const kpoaCategoryLabel = (category: KpoaIncidentRow['kpoaCategory']): string =>
  category === 'illness' ? 'เจ็บป่วย' : 'บาดเจ็บ';

const endOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const eventTime = (row: KpoaIncidentRow): number => {
  const eventDate = toDateFromFirestore(row.eventAt);
  return eventDate ? eventDate.getTime() : 0;
};

export function filterKpoaRowsByDateRange(
  rows: readonly KpoaIncidentRow[],
  startDate: Date,
  endDate: Date,
): KpoaIncidentRow[] {
  const startMs = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()).getTime();
  const endMs = endOfDay(endDate).getTime();

  return rows.filter((row) => {
    const eventDate = toDateFromFirestore(row.eventAt);
    if (!eventDate) return false;
    const time = eventDate.getTime();
    return time >= startMs && time <= endMs;
  });
}

export function buildKpoaExportData(
  rows: readonly KpoaIncidentRow[],
  deptIdToName: Map<string, string>,
): { headers: string[]; rows: string[][] } {
  const sortedRows = [...rows].sort((a, b) => {
    const diff = eventTime(b) - eventTime(a);
    if (diff !== 0) return diff;
    return (a.id ?? '').localeCompare(b.id ?? '', 'th');
  });

  const dataRows = sortedRows.map((row) => {
    const eventDate = toDateFromFirestore(row.eventAt);
    return [
      eventDate ? formatMedErrorEventDate(eventDate) : row.eventDateRaw ?? '—',
      kpoaCategoryLabel(row.kpoaCategory),
      row.eventYearMonth ?? '—',
      row.fiscalQuarter || '—',
      row.fiscalYear > 0 ? String(row.fiscalYear) : '—',
      deptIdToName.get(row.departmentId) ?? 'ไม่ทราบชื่อหน่วยงาน',
      row.wardRaw ?? '—',
      row.ipdOpd || '—',
      row.role || '—',
      row.injuryType || '—',
      row.severity || '—',
      row.causePrimary || '—',
      formatOptionalNumber(row.experienceYears),
      formatOptionalNumber(row.ageYears),
      row.notes ?? '—',
    ];
  });

  return { headers: KPOA_EXPORT_HEADERS, rows: dataRows };
}
